import { useEffect, useRef } from 'react';
import { useWebSocket } from '../context/WebSocketContext';

// ─────────────────────────────────────────────────────────────────────────────
// 「剛剛（重新）連上了，去補抓一次」的訂閱點。
//
// 【為什麼一定要有這支】WebSocket **沒有補送機制**：斷線期間伺服器發出的訊息是當場丟掉的，
// 連回來也不會補。2026-08-18 正式站日誌就是這個形狀——
//   11:41:04 班級分流廣播：班級 42，發送 1 個，跳過 0 個   ← 有人在線，送到了
//   11:41:21 班級分流廣播：班級 42，發送 0 個，跳過 0 個   ← 連線清單是空的，訊息無處可送
// 「跳過 0 個」證明不是班級對不上被略過，而是那一刻根本沒有連線；同一位老師的連線日誌則是
// 1.5 秒內重連三次。連線永遠不可能 100% 穩定，所以正確的設計不是把 WS 修到不斷線，而是
// **不依賴 WS 的完整性**：把它當加速器而非唯一來源，斷線一恢復就用 HTTP 重新對一次帳。
//
// 【為什麼是遞增數字（connectionEpoch）而不是 onReconnect 事件】事件是「發生當下有訂閱才
// 收得到」，晚一步掛載的元件永遠錯過；數字是狀態，任何時候掛載都能拿它跟自己記下的基準
// 比對，也不必維護一份訂閱者清單。
//
// 【第一次觀察到的值刻意跳過】消費端掛載時本來就會自己做一次初始載入，開頁當下再補抓一次
// 只是重複請求。代價是「掛載時 WS 還沒連上」（冷啟動）會多打一次 —— 刻意接受：多一次 GET
// 遠比為了省它而讓「初始載入與第一則訊息擦身而過」這種競態活下來划算。
//
// 【為什麼放在 src/hooks/ 而不是 WebSocketContext.jsx】那個檔案已經因為 useWebSocket 觸到
// react-refresh/only-export-components（元件檔不該再匯出非元件），再加一個匯出就多一個
// 同樣的 error。這裡也與 src/hooks/useStickToBottom.js 的位置慣例一致。
//
// 用法（onReconnect 傳行內箭頭函式也安全，見下方 epoch 比對）：
//   useSocketReconnect(fetchLatest);
// ─────────────────────────────────────────────────────────────────────────────
export default function useSocketReconnect(onReconnect) {
    const ws = useWebSocket();
    const epoch = ws?.connectionEpoch;
    // 掛載當下看到的 epoch 當基準（沒有 Provider 時是 undefined，下面的比對自然永不成立）
    const seenRef = useRef(epoch);

    useEffect(() => {
        // 沒變就什麼都不做。這道比對同時擋掉「只是 onReconnect 換了函式參考」造成的 effect
        // 重跑——所以呼叫端不必為了它多包一層 useCallback，一次重連也只會補抓一次。
        if (seenRef.current === epoch) return;
        seenRef.current = epoch;
        onReconnect?.();
    }, [epoch, onReconnect]);
}
